import React, { useEffect, useState, Children, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, AlertTriangle, TrendingUp, DollarSign, Ghost, Zap, Lock, Download, ShieldCheck, ChevronDown, ChevronUp, PlayCircle, Smartphone } from 'lucide-react';
// --- Meta Pixel Helper ---
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: any;
  }
}
const initMetaPixel = () => {
  if (typeof window === 'undefined') return;
  (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s);
    } else {
      b.head.appendChild(t);
    }
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', '898349736246164');
  window.fbq('track', 'PageView');
};
const trackAddToCart = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddToCart', {
      value: 6.36,
      currency: 'USD',
      content_name: 'Faceless Cash: El Método de Clonación Viral'
    });
  }
};
const goToCheckout = () => {
  trackAddToCart();
  window.open('https://www.factoravance.com/cart/51548141486400:1', '_blank');
};
// --- Animation Variants ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const pulseAnimation = {
  scale: [1, 1.03, 1],
  transition: {
    repeat: Infinity,
    duration: 2
  }
};
// --- Components ---
const UrgencyBar = () => <motion.div initial={{
  y: -50
}} animate={{
  y: 0
}} className="fixed top-0 left-0 w-full z-50 bg-[#ff3d3d] text-white py-2 px-4 text-center font-bold text-sm md:text-base shadow-lg" data-id="element-1">
    <motion.div animate={{
    opacity: [1, 0.7, 1]
  }} transition={{
    repeat: Infinity,
    duration: 1.5
  }} className="flex items-center justify-center gap-2" data-id="element-2">
      <AlertTriangle size={16} fill="white" data-id="element-3" />
      <span data-id="element-4">
        ⚡ OFERTA LIMITADA: Solo quedan 47 copias al precio de $6.36 USD ⚡
      </span>
    </motion.div>
  </motion.div>;
const HeroSection = () => <section className="relative pt-16 pb-10 md:pt-32 md:pb-24 px-4 overflow-hidden min-h-screen flex flex-col justify-center items-center text-center bg-[#080808]" data-id="element-5">
    {/* Background Texture */}
    <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
    backgroundImage: 'radial-gradient(#333 1px, transparent 1px)',
    backgroundSize: '30px 30px'
  }} data-id="element-6" />

    <div className="max-w-4xl mx-auto relative z-10" data-id="element-7">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="inline-block mb-4 px-3 py-1 border border-[#f5a623] rounded-full bg-[#f5a623]/10" data-id="element-8">
        <span className="text-[#f5a623] font-bold tracking-wider text-xs md:text-base uppercase" data-id="element-9">
          ★ El Método Faceless #1 en LATAM
        </span>
      </motion.div>

      <motion.h1 initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="text-4xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.95] mb-4 tracking-tight" data-id="element-10">
        CÓMO GANAR TUS PRIMEROS <span className="text-[#00e676]" data-id="element-11">$500 USD</span>{' '}
        AL MES CON TIKTOK{' '}
        <span className="text-[#f5a623]" data-id="element-12">SIN GRABAR UN SOLO VIDEO.</span>
      </motion.h1>

      <motion.p initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="text-base md:text-2xl text-gray-300 mb-6 max-w-2xl mx-auto font-light" data-id="element-13">
        El método{' '}
        <span className="text-white font-semibold italic" data-id="element-14">"invisible"</span>{' '}
        para monetizar la IA que los influencers no quieren que sepas.
      </motion.p>

      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={staggerContainer} className="flex flex-wrap justify-center gap-2 md:gap-6 mb-7" data-id="element-15">
        {['Sin mostrar tu cara', 'Sin equipo costoso', 'Sin experiencia previa'].map((item, i) => <motion.div key={i} variants={fadeInUp} className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10" data-id="element-16">
            <Check size={14} className="text-[#00e676]" data-id="element-17" />
            <span className="text-xs md:text-base font-medium text-gray-200" data-id="element-18">
              {item}
            </span>
          </motion.div>)}
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} data-id="element-19">
        <motion.button animate={pulseAnimation} onClick={() => document.getElementById('offer-section')?.scrollIntoView({
        behavior: 'smooth'
      })} className="w-full md:w-auto bg-[#00e676] hover:bg-[#00c853] text-black font-black text-lg md:text-3xl py-3.5 px-6 md:px-12 rounded-xl shadow-[0_0_30px_rgba(0,230,118,0.4)] transition-all transform hover:-translate-y-1 border-b-4 border-[#00b248] cursor-pointer" data-id="element-20">
          SÍ, QUIERO MI FÁBRICA DE CASH →
        </motion.button>
        <p className="mt-3 text-xs text-gray-500 flex items-center justify-center gap-2" data-id="element-21">
          <Lock size={11} data-id="element-22" /> Pago 100% seguro · Acceso inmediato · PDF
          descargable
        </p>
      </motion.div>
    </div>
  </section>;
const PainSection = () => <section id="pain-section" className="py-12 px-4 bg-[#0d0d0d]" data-id="element-23">
    <div className="max-w-3xl mx-auto" data-id="element-24">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="bg-[#151515] border border-white/5 rounded-2xl p-6 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-25">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#f5a623]/5 rounded-full blur-3xl -mr-16 -mt-16" data-id="element-26"></div>

        <h2 className="text-3xl md:text-5xl text-[#f5a623] mb-6 text-center" data-id="element-27">
          ¿TE SUENA FAMILIAR?
        </h2>

        <div className="space-y-4" data-id="element-28">
          {['Trabajas duro pero tu cuenta bancaria no sube.', 'Ves a otros ganar miles en TikTok y no entiendes cómo lo hacen.', 'Quieres el dinero pero odias la idea de poner tu cara en cámara.', 'Probaste otros métodos y solo perdiste tiempo y dinero.', 'La inflación te come el sueldo mes a mes.'].map((pain, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex items-start gap-3" data-id="element-29">
              <div className="bg-red-500/10 p-1 rounded-full mt-0.5 shrink-0" data-id="element-30">
                <X size={16} className="text-[#ff3d3d]" data-id="element-31" />
              </div>
              <p className="text-base text-gray-300" data-id="element-32">{pain}</p>
            </motion.div>)}
        </div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="mt-7 text-center text-[#f5a623] text-base md:text-xl italic font-medium border-t border-white/10 pt-6" data-id="element-33">
          "No es tu culpa. Nadie te enseñó el sistema correcto."
        </motion.p>
      </motion.div>
    </div>
  </section>;
const RevelationSection = () => <section className="py-12 px-4 bg-[#080808] relative" data-id="element-34">
    <div className="max-w-5xl mx-auto" data-id="element-35">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="text-center mb-10" data-id="element-36">
        <h2 className="text-3xl md:text-6xl text-white mb-4" data-id="element-37">
          LA VERDAD QUE LOS INFLUENCERS OCULTAN
        </h2>
        <p className="text-xl md:text-3xl text-[#f5a623] font-bold max-w-3xl mx-auto leading-tight" data-id="element-38">
          Los videos que más dinero generan hoy{' '}
          <span className="underline decoration-[#f5a623] decoration-4 underline-offset-4" data-id="element-39">
            NO tienen rostro.
          </span>
        </p>
        <p className="mt-4 text-gray-400 text-base max-w-2xl mx-auto" data-id="element-40">
          Son cuentas que usan voces ultra-realistas, imágenes cinemáticas y
          guiones psicológicamente diseñados.{' '}
          <span className="text-white font-semibold" data-id="element-41">
            Y lo mejor: Todo es creado por Inteligencia Artificial.
          </span>
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-42">
        {[{
        icon: <Zap size={28} className="text-[#f5a623]" data-id="element-43" />,
        title: 'IA Generativa',
        desc: 'Crea contenido 24/7 sin que tú estés presente ni grabes nada.'
      }, {
        icon: <DollarSign size={28} className="text-[#f5a623]" data-id="element-44" />,
        title: 'Ingresos en USD',
        desc: 'Cobra en dólares desde cualquier país de LATAM directo a tu cuenta.'
      }, {
        icon: <Ghost size={28} className="text-[#f5a623]" data-id="element-45" />,
        title: '100% Anónimo',
        desc: 'Sin cámara, sin micrófono, sin excusas. Nadie sabrá que eres tú.'
      }].map((card, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.2
      }} className="bg-[#111] border border-[#f5a623]/30 p-5 rounded-xl hover:border-[#f5a623] transition-colors group" data-id="element-46">
            <div className="bg-[#f5a623]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-47">
              {card.icon}
            </div>
            <h3 className="text-xl text-white mb-2" data-id="element-48">{card.title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed" data-id="element-49">{card.desc}</p>
          </motion.div>)}
      </div>
    </div>
  </section>;
const MechanismSection = () => <section className="py-12 px-4 bg-[#0d0d0d] border-y border-white/5" data-id="element-50">
    <div className="max-w-5xl mx-auto" data-id="element-51">
      <div className="text-center mb-10" data-id="element-52">
        <span className="text-[#00e676] font-bold tracking-widest uppercase text-xs mb-2 block" data-id="element-53">
          EL SISTEMA
        </span>
        <h2 className="text-4xl md:text-7xl text-white mb-3" data-id="element-54">
          FACELESS CASH: EL MÉTODO DE CLONACIÓN
        </h2>
        <p className="text-base text-gray-400" data-id="element-55">
          No es un curso de marketing aburrido. Es un manual de ingeniería
          inversa.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 relative" data-id="element-56">
        <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#f5a623]/50 to-transparent z-0" data-id="element-57"></div>

        {[{
        step: '01',
        title: 'DETECTAR',
        desc: 'Identificamos qué contenido está viral usando herramientas gratuitas.'
      }, {
        step: '02',
        title: 'CLONAR',
        desc: 'Replicamos la estructura con IA sin copiar (100% legal y original).'
      }, {
        step: '03',
        title: 'MONETIZAR',
        desc: 'Activamos los ingresos: afiliados, TikTok Shop, sponsorships.'
      }].map((item, i) => <motion.div key={i} initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.2
      }} className="relative z-10 bg-[#1a1a1a] p-6 rounded-2xl border border-white/5 text-center" data-id="element-58">
            <div className="w-16 h-16 mx-auto bg-[#080808] border-2 border-[#f5a623] rounded-full flex items-center justify-center text-2xl font-bold text-[#f5a623] mb-4 shadow-[0_0_20px_rgba(245,166,35,0.2)]" data-id="element-59">
              {item.step}
            </div>
            <h3 className="text-2xl text-white mb-2" data-id="element-60">{item.title}</h3>
            <p className="text-gray-400 text-sm" data-id="element-61">{item.desc}</p>
          </motion.div>)}
      </div>
    </div>
  </section>;
const OfferSection = () => <section id="offer-section" className="py-12 px-4 bg-[#080808]" data-id="element-62">
    <div className="max-w-4xl mx-auto" data-id="element-63">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="bg-gradient-to-b from-[#111] to-[#080808] border border-white/10 rounded-3xl p-6 md:p-12 shadow-2xl" data-id="element-64">
        <div className="text-center mb-7" data-id="element-65">
          <h2 className="text-3xl md:text-5xl text-white mb-3" data-id="element-66">
            ¿QUÉ HAY DENTRO DEL PDF?
          </h2>
          <p className="text-[#f5a623] text-base font-medium" data-id="element-67">
            Un manual de implementación rápida, no teoría inútil.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-8 mb-8" data-id="element-68">
          {['La estrategia exacta para que el algoritmo de TikTok te ame en 7 días', 'Cómo configurar tus 3 Obreros Digitales (IAs que trabajan por ti)', 'El truco para cobrar en dólares sin importar tu país', 'Cómo clonar videos virales legalmente con ChatGPT + ElevenLabs', 'La guía de nichos: cuáles pagan más y por qué', 'El calendario de contenido: 30 días de videos en una tarde', 'Los 5 errores que matan canales faceless (y cómo evitarlos)'].map((feature, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -10
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex items-start gap-2.5" data-id="element-69">
              <Check size={17} className="text-[#00e676] mt-0.5 shrink-0" data-id="element-70" />
              <span className="text-gray-300 text-sm" data-id="element-71">{feature}</span>
            </motion.div>)}
        </div>

        <div className="text-center border-t border-white/10 pt-6" data-id="element-72">
          <p className="text-gray-500 text-base line-through mb-1" data-id="element-73">
            Valor real: $97 USD
          </p>
          <p className="text-3xl md:text-5xl text-white font-bold mb-6" data-id="element-74">
            HOY: <span className="text-[#00e676]" data-id="element-75">$6.36 USD</span>
          </p>
          <motion.button animate={pulseAnimation} onClick={goToCheckout} className="w-full md:w-auto bg-[#00e676] hover:bg-[#00c853] text-black font-black text-lg py-3.5 px-8 rounded-xl shadow-[0_0_20px_rgba(0,230,118,0.3)] transition-all cursor-pointer" data-id="element-76">
            DESCARGAR AHORA MISMO
          </motion.button>
        </div>
      </motion.div>
    </div>
  </section>;
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-white/10" data-id="element-77">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex justify-between items-center text-left focus:outline-none" data-id="element-78">
        <span className="text-xl text-white font-medium pr-4" data-id="element-79">{question}</span>
        {isOpen ? <ChevronUp className="text-[#f5a623]" data-id="element-80" /> : <ChevronDown className="text-[#f5a623]" data-id="element-81" />}
      </button>
      <AnimatePresence data-id="element-82">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-83">
            <p className="text-gray-400 pb-6 leading-relaxed" data-id="element-84">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const FAQSection = () => <section className="py-12 px-4 bg-[#0d0d0d]" data-id="element-85">
    <div className="max-w-3xl mx-auto" data-id="element-86">
      <h2 className="text-3xl md:text-5xl text-white text-center mb-8" data-id="element-87">
        TUS DUDAS, RESUELTAS
      </h2>
      <div className="space-y-1" data-id="element-88">
        <FAQItem question="¿Necesito experiencia previa?" answer="No. El PDF está diseñado desde cero para alguien que nunca ha creado contenido en su vida. Te llevamos paso a paso." data-id="element-89" />
        <FAQItem question="¿Es legal clonar videos?" answer="Sí, absolutamente. No descargamos ni resubimos contenido de otros. Clonamos la ESTRUCTURA y los ganchos psicológicos, pero el contenido lo genera la IA de forma original. Es como aprender la receta de un chef y cocinarla tú mismo." data-id="element-90" />
        <FAQItem question="¿Cuánto tiempo necesito al día?" answer="Al principio te tomará unas 2 horas configurar todo. Una vez que tengas tus 'Obreros Digitales' listos, con 30-60 minutos al día puedes mantener el sistema funcionando." data-id="element-91" />
        <FAQItem question="¿Funciona en mi país?" answer="Sí. El método funciona globalmente. Te enseñamos cómo configurar las cuentas para que lleguen a la audiencia correcta (EEUU o LATAM) y cómo cobrar en dólares desde México, Colombia, Argentina, Chile, Perú, España, etc." data-id="element-92" />
      </div>
    </div>
  </section>;
const FinalCTA = () => <section id="final-cta" className="py-14 px-4 bg-[#080808] relative overflow-hidden" data-id="element-93">
    <div className="absolute inset-0 bg-gradient-to-t from-[#f5a623]/10 to-transparent pointer-events-none" data-id="element-94"></div>

    <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-95">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true
    }} variants={fadeInUp} className="bg-[#111] border-2 border-[#f5a623] rounded-3xl p-6 md:p-16 shadow-[0_0_50px_rgba(245,166,35,0.15)]" data-id="element-96">
        <h2 className="text-4xl md:text-7xl text-white mb-4" data-id="element-97">
          TU FÁBRICA DE CASH TE ESPERA
        </h2>
        <p className="text-base md:text-xl text-gray-400 mb-7 max-w-2xl mx-auto" data-id="element-98">
          ¿Vas a seguir haciendo scroll infinito viendo cómo otros ganan dinero,
          o vas a ser tú quien reciba los pagos?
        </p>

        <div className="flex flex-col items-center justify-center mb-7" data-id="element-99">
          <span className="text-gray-500 text-base line-through mb-1" data-id="element-100">
            $97 USD valor real
          </span>
          <span className="text-6xl md:text-8xl font-bold text-[#00e676] tracking-tighter" data-id="element-101">
            $6.36 USD
          </span>
        </div>

        <motion.button animate={pulseAnimation} onClick={goToCheckout} className="w-full md:w-auto bg-[#00e676] hover:bg-[#00c853] text-black font-black text-xl md:text-3xl py-4 px-10 md:px-12 rounded-xl shadow-[0_0_40px_rgba(0,230,118,0.5)] transition-all transform hover:-translate-y-1 mb-6 cursor-pointer" data-id="element-102">
          SÍ, QUIERO MI FÁBRICA DE CASH →
        </motion.button>

        <div className="flex flex-wrap justify-center gap-3 md:gap-8 text-xs md:text-sm text-gray-400 mb-6" data-id="element-103">
          <span className="flex items-center gap-1.5" data-id="element-104">
            <ShieldCheck size={14} data-id="element-105" /> Pago Seguro
          </span>
          <span className="flex items-center gap-1.5" data-id="element-106">
            <Smartphone size={14} data-id="element-107" /> Acceso Inmediato
          </span>
          <span className="flex items-center gap-1.5" data-id="element-108">
            <Download size={14} data-id="element-109" /> PDF Descargable
          </span>
        </div>

        <div className="bg-white/5 rounded-lg p-3 max-w-lg mx-auto border border-white/10" data-id="element-110">
          <p className="text-xs text-gray-300" data-id="element-111">
            <span className="text-[#f5a623] font-bold" data-id="element-112">
              GARANTÍA DE 30 DÍAS:
            </span>{' '}
            Si aplicas el método y no ves resultados, te devolvemos el 100% de
            tu dinero. Sin preguntas incómodas.
          </p>
        </div>
      </motion.div>
    </div>
  </section>;
// --- Legal Modal ---
type ModalType = 'terms' | 'privacy' | null;
const termsContent = `TÉRMINOS Y CONDICIONES

Última actualización: 2024

1. ACEPTACIÓN DE LOS TÉRMINOS
Al adquirir y utilizar el producto digital "Faceless Cash: El Método de Clonación Viral" (en adelante, "el Producto"), usted acepta quedar vinculado por estos Términos y Condiciones. Si no está de acuerdo con alguno de estos términos, no utilice el Producto.

2. DESCRIPCIÓN DEL PRODUCTO
El Producto es una guía digital en formato PDF que contiene información educativa sobre estrategias de creación de contenido digital y monetización en plataformas de redes sociales. El contenido es de carácter informativo y educativo únicamente.

3. LICENCIA DE USO
Al adquirir el Producto, se le otorga una licencia personal, no exclusiva e intransferible para usar el contenido únicamente para sus fines personales. Queda estrictamente prohibido:
- Reproducir, distribuir o revender el Producto total o parcialmente.
- Compartir el acceso o el archivo con terceros.
- Utilizar el contenido para crear productos derivados con fines comerciales.

4. PROPIEDAD INTELECTUAL
Todo el contenido del Producto, incluyendo textos, gráficos, logotipos e imágenes, es propiedad exclusiva de Faceless Cash y está protegido por las leyes de propiedad intelectual aplicables.

5. GARANTÍA DE SATISFACCIÓN
Ofrecemos una garantía de devolución de dinero de 30 días. Si aplica las estrategias del Producto y no obtiene resultados, puede solicitar un reembolso completo dentro de los 30 días posteriores a su compra.

6. LIMITACIÓN DE RESPONSABILIDAD
Los resultados descritos en el material de marketing son ejemplos de lo que es posible, no una garantía de resultados. El éxito individual depende de muchos factores, incluyendo el esfuerzo, la dedicación y las condiciones del mercado. Faceless Cash no garantiza ingresos específicos.

7. MODIFICACIONES
Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación.

8. LEY APLICABLE
Estos términos se rigen por las leyes aplicables en la jurisdicción del vendedor. Cualquier disputa será resuelta mediante arbitraje vinculante.`;
const privacyContent = `POLÍTICA DE PRIVACIDAD

Última actualización: 2024

1. INFORMACIÓN QUE RECOPILAMOS
Al realizar una compra, recopilamos la información necesaria para procesar su pedido, incluyendo nombre, dirección de correo electrónico e información de pago. Esta información es procesada de forma segura a través de nuestra plataforma de pago (Shopify).

2. USO DE LA INFORMACIÓN
Utilizamos su información personal para:
- Procesar y completar su compra.
- Enviarle el Producto digital adquirido.
- Enviarle comunicaciones relacionadas con su compra.
- Mejorar nuestros productos y servicios.
- Cumplir con obligaciones legales.

3. PROTECCIÓN DE DATOS
Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción. Todos los pagos son procesados mediante cifrado SSL.

4. COOKIES Y TECNOLOGÍAS DE SEGUIMIENTO
Nuestro sitio web utiliza cookies y tecnologías similares, incluyendo el Meta Pixel de Facebook, para mejorar su experiencia de navegación y mostrarle publicidad relevante. Puede configurar su navegador para rechazar cookies, aunque esto puede afectar la funcionalidad del sitio.

5. COMPARTIR INFORMACIÓN CON TERCEROS
No vendemos, intercambiamos ni transferimos su información personal a terceros, excepto cuando sea necesario para procesar su compra (procesadores de pago) o cuando lo exija la ley.

6. RETENCIÓN DE DATOS
Conservamos su información personal durante el tiempo necesario para cumplir con los propósitos descritos en esta política, a menos que la ley exija un período de retención más largo.

7. SUS DERECHOS
Usted tiene derecho a:
- Acceder a la información personal que tenemos sobre usted.
- Solicitar la corrección de información inexacta.
- Solicitar la eliminación de su información personal.
- Oponerse al procesamiento de su información.

8. CAMBIOS A ESTA POLÍTICA
Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos sobre cambios significativos publicando la nueva política en nuestro sitio web.`;
const LegalModal = ({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) => {
  const isTerms = type === 'terms';
  const title = isTerms ? 'Términos y Condiciones' : 'Política de Privacidad';
  const content = isTerms ? termsContent : privacyContent;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-113">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-114" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="relative w-full max-w-2xl max-h-[80vh] bg-[#111] border border-[#f5a623]/30 rounded-2xl shadow-[0_0_60px_rgba(245,166,35,0.15)] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-115">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 shrink-0" data-id="element-116">
          <h2 className="text-2xl text-[#f5a623]" data-id="element-117">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors text-gray-400 hover:text-white" data-id="element-118">
            <X size={16} data-id="element-119" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-5 flex-1" data-id="element-120">
          <div className="text-gray-400 text-sm leading-relaxed whitespace-pre-line font-sans" data-id="element-121">
            {content}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 shrink-0" data-id="element-122">
          <button onClick={onClose} className="w-full bg-[#f5a623]/10 hover:bg-[#f5a623]/20 border border-[#f5a623]/30 text-[#f5a623] font-semibold py-2.5 rounded-xl transition-colors text-sm" data-id="element-123">
            Cerrar
          </button>
        </div>
      </motion.div>
    </motion.div>;
};
const Footer = () => {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-black py-10 text-center text-gray-600 text-sm" data-id="element-124">
        <p className="mb-4" data-id="element-125">
          © 2024 Faceless Cash. Todos los derechos reservados.
        </p>
        <div className="flex justify-center gap-6" data-id="element-126">
          <button onClick={() => setActiveModal('terms')} className="hover:text-gray-400 transition-colors cursor-pointer underline-offset-2 hover:underline" data-id="element-127">
            Términos y Condiciones
          </button>
          <button onClick={() => setActiveModal('privacy')} className="hover:text-gray-400 transition-colors cursor-pointer underline-offset-2 hover:underline" data-id="element-128">
            Política de Privacidad
          </button>
        </div>
        <p className="mt-8 text-xs max-w-2xl mx-auto px-4 opacity-50" data-id="element-129">
          Descargo de responsabilidad: Los resultados pueden variar. Este
          producto no garantiza ingresos. El éxito depende de tu dedicación y
          aplicación de las estrategias enseñadas. Este sitio no es parte del
          sitio web de TikTok o ByteDance Ltd. Además, este sitio NO está
          respaldado por TikTok de ninguna manera.
        </p>
      </footer>

      <AnimatePresence data-id="element-130">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-131" />}
      </AnimatePresence>
    </>;
};
export function SalesPage() {
  useEffect(() => {
    initMetaPixel();
  }, []);
  return <div className="min-h-screen bg-[#080808] text-white overflow-x-hidden font-sans selection:bg-[#f5a623] selection:text-black" data-id="element-132">
      <UrgencyBar data-id="element-133" />
      <HeroSection data-id="element-134" />
      <PainSection data-id="element-135" />
      <RevelationSection data-id="element-136" />
      <MechanismSection data-id="element-137" />
      <OfferSection data-id="element-138" />
      <FAQSection data-id="element-139" />
      <FinalCTA data-id="element-140" />
      <Footer data-id="element-141" />
    </div>;
}